/**
 * Loading skeleton components with Panerai dark theme
 * Provides shimmer animations for loading states
 */

/**
 * Base skeleton element with shimmer animation
 */
export function Skeleton({ className = '', rounded = 'rounded', shimmer = true }) {
  if (shimmer) {
    return (
      <div
        className={`relative overflow-hidden bg-dial-elevated ${rounded} ${className}`}
        aria-hidden="true"
      >
        <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-dial-border/50 to-transparent" />
      </div>
    )
  }

  return (
    <div
      className={`animate-pulse bg-dial-elevated ${rounded} ${className}`}
      aria-hidden="true"
    />
  )
}

/**
 * Text line skeleton
 */
export function SkeletonText({ lines = 1, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-4 ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`}
        />
      ))}
    </div>
  )
}

/**
 * Avatar/circle skeleton
 */
export function SkeletonAvatar({ size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  }

  return (
    <Skeleton
      className={`${sizeClasses[size]} ${className}`}
      rounded="rounded-full"
    />
  )
}

/**
 * Card skeleton - matches the card pattern used throughout the app
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={`bg-dial-surface border border-dial-border rounded-xl p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <SkeletonAvatar size="lg" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  )
}

/**
 * List item skeleton
 */
export function SkeletonListItem({ hasAvatar = true, className = '' }) {
  return (
    <div className={`flex items-center gap-3 p-3 ${className}`}>
      {hasAvatar && <SkeletonAvatar />}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-3/4" />
      </div>
      <Skeleton className="h-4 w-16" />
    </div>
  )
}

/**
 * Stats card skeleton
 */
export function SkeletonStats({ className = '' }) {
  return (
    <div className={`bg-dial-surface border border-dial-border rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10 rounded-lg" />
        <div className="flex-1 space-y-1">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-6 w-12" />
        </div>
      </div>
    </div>
  )
}

/**
 * Table skeleton
 */
export function SkeletonTable({ rows = 5, columns = 4, className = '' }) {
  return (
    <div className={`bg-dial-surface border border-dial-border rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex gap-4 p-4 border-b border-dial-border bg-dial-elevated">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex gap-4 p-4 border-b border-dial-border last:border-b-0"
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              className={`h-4 flex-1 ${colIndex === 0 ? 'w-1/4' : ''}`}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Chat message skeleton
 */
export function SkeletonChatMessage({ isUser = false, className = '' }) {
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} ${className}`}>
      <div
        className={`max-w-[80%] p-3 rounded-2xl ${
          isUser ? 'bg-lume-teal/20' : 'bg-dial-elevated'
        }`}
      >
        <div className="space-y-2">
          <Skeleton className={`h-4 w-48 ${isUser ? 'bg-lume-teal/30' : 'bg-dial-border'}`} shimmer={false} />
          <Skeleton className={`h-4 w-32 ${isUser ? 'bg-lume-teal/30' : 'bg-dial-border'}`} shimmer={false} />
        </div>
      </div>
    </div>
  )
}

/**
 * Form skeleton
 */
export function SkeletonForm({ fields = 3, className = '' }) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      ))}
      <Skeleton className="h-12 w-full rounded-xl mt-6" />
    </div>
  )
}

/**
 * Dashboard skeleton - full page loading state
 */
export function SkeletonDashboard() {
  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
        <SkeletonAvatar size="lg" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <SkeletonStats />
        <SkeletonStats />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <Skeleton className="w-12 h-12 rounded-xl" />
            <Skeleton className="h-3 w-12" />
          </div>
        ))}
      </div>

      {/* List section */}
      <div className="bg-dial-surface border border-dial-border rounded-xl">
        <div className="p-4 border-b border-dial-border">
          <Skeleton className="h-5 w-32" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonListItem key={i} className="border-b border-dial-border last:border-b-0" />
        ))}
      </div>
    </div>
  )
}

/**
 * Client detail skeleton
 */
export function SkeletonClientDetail() {
  return (
    <div className="space-y-4 p-4">
      {/* Profile header */}
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4">
        <div className="flex items-center gap-4">
          <SkeletonAvatar size="xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-32" />
            <div className="flex gap-2 mt-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <SkeletonStats />
        <SkeletonStats />
        <SkeletonStats />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-dial-surface border border-dial-border rounded-xl p-3">
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <Skeleton className="h-3 w-14" />
            </div>
          </div>
        ))}
      </div>

      {/* Notes section */}
      <div className="bg-dial-surface border border-dial-border rounded-xl p-4 space-y-3">
        <Skeleton className="h-5 w-24" />
        <SkeletonText lines={3} />
      </div>
    </div>
  )
}

/**
 * Full page loading spinner
 */
export function LoadingSpinner({ size = 'md', className = '' }) {
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <svg
        className={`animate-spin text-lume-teal ${sizeClasses[size]}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  )
}

/**
 * Full page loading state
 */
export function PageLoading({ message = 'Loading...' }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-dial-black">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-indices-muted">{message}</p>
    </div>
  )
}

/**
 * Inline loading state
 */
export function InlineLoading({ className = '' }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LoadingSpinner size="sm" />
      <span className="text-sm text-indices-muted">Loading...</span>
    </div>
  )
}

export default {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonListItem,
  SkeletonStats,
  SkeletonTable,
  SkeletonChatMessage,
  SkeletonForm,
  SkeletonDashboard,
  SkeletonClientDetail,
  LoadingSpinner,
  PageLoading,
  InlineLoading,
}
